import { useState, type FormEvent } from "react";
import { PublicLayout } from "../../components/public-layout.js";
import { HeroSection } from "../../components/marketing-components.js";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FormState {
  name: string;
  email: string;
  company: string;
  topic: string;
  message: string;
}

type SubmitStatus = "idle" | "submitting" | "success" | "error";

export function ContactPage() {
  const [form, setForm] = useState<FormState>({
    name: "",
    email: "",
    company: "",
    topic: "",
    message: "",
  });
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);

  function handleChange(
    e: FormEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) {
    const target = e.currentTarget;
    setForm((prev) => ({ ...prev, [target.id]: target.value }));
    if (target.id === "email" && emailError) {
      setEmailError(null);
    }
  }

  function handleEmailBlur() {
    const trimmed = form.email.trim();
    if (trimmed && !EMAIL_RE.test(trimmed)) {
      setEmailError("Enter a valid email address");
    } else {
      setEmailError(null);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus("submitting");
    setErrorMessage("");

    try {
      const res = await fetch("/api/public/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        const data = (await res.json()) as { message?: string };
        throw new Error(data.message ?? "Failed to send message");
      }

      setStatus("success");
      setForm({ name: "", email: "", company: "", topic: "", message: "" });
    } catch (err) {
      setStatus("error");
      setErrorMessage(
        err instanceof Error ? err.message : "Something went wrong",
      );
    }
  }

  return (
    <PublicLayout>
      <HeroSection
        title="Get in touch"
        subtitle="Questions? Want a demo? Let's talk."
      />

      <section className="marketing-section">
        <div className="section-content">
          <div className="contact-form-container">
            <div className="contact-info">
              <h2>Contact information</h2>

              <div className="contact-detail">
                <h4>Email</h4>
                <p>
                  <a href="mailto:hello@accessimonitor.app">
                    hello@accessimonitor.app
                  </a>
                </p>
              </div>

              <div className="contact-detail">
                <h4>Support</h4>
                <p>
                  <a href="mailto:support@accessimonitor.app">
                    support@accessimonitor.app
                  </a>
                </p>
              </div>

              <div className="contact-detail">
                <h4>Location</h4>
                <p>Available globally. We respond during business hours UTC.</p>
              </div>

              <div className="contact-detail">
                <h4>General enquiries</h4>
                <p>
                  Sales, partnerships, or press inquiries: email us with a brief
                  description of your needs.
                </p>
              </div>
            </div>

            <div className="contact-form">
              <h2>Send us a message</h2>

              {status === "success" && (
                <div className="alert alert--success" role="alert">
                  Thanks for reaching out! We'll get back to you within 24
                  hours.
                </div>
              )}

              {status === "error" && (
                <div className="alert alert--error" role="alert">
                  {errorMessage || "Failed to send message. Please try again."}
                </div>
              )}

              <form onSubmit={(e) => void handleSubmit(e)}>
                <div className="form-group">
                  <label htmlFor="name">Name *</label>
                  <input
                    id="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={handleChange}
                    aria-label="Your name"
                    disabled={status === "submitting"}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    onBlur={handleEmailBlur}
                    aria-label="Your email address"
                    aria-invalid={emailError ? true : undefined}
                    aria-describedby={emailError ? "email-error" : undefined}
                    disabled={status === "submitting"}
                  />
                  {emailError && (
                    <p
                      id="email-error"
                      className="form-field__error"
                      role="alert"
                    >
                      {emailError}
                    </p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="company">Company</label>
                  <input
                    id="company"
                    type="text"
                    value={form.company}
                    onChange={handleChange}
                    aria-label="Your company name (optional)"
                    disabled={status === "submitting"}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="topic">Topic *</label>
                  <select
                    id="topic"
                    required
                    value={form.topic}
                    onChange={handleChange}
                    aria-label="Select a topic"
                    disabled={status === "submitting"}
                  >
                    <option value="">Select a topic</option>
                    <option value="demo">Book a demo</option>
                    <option value="support">Get support</option>
                    <option value="sales">Sales inquiry</option>
                    <option value="partnership">Partnership</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    rows={5}
                    required
                    value={form.message}
                    onChange={handleChange}
                    aria-label="Your message"
                    disabled={status === "submitting"}
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={status === "submitting"}
                >
                  {status === "submitting" ? "Sending..." : "Send message"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-section marketing-section--alt">
        <div className="section-content">
          <h2>What to expect</h2>

          <p>When you reach out, here's what happens:</p>

          <div className="timeline-simple">
            <div className="step-simple">
              <span className="step-number">1</span>
              <div>
                <h4>We receive your message</h4>
                <p>Usually within minutes during business hours</p>
              </div>
            </div>

            <div className="step-simple">
              <span className="step-number">2</span>
              <div>
                <h4>Initial response</h4>
                <p>
                  We'll confirm receipt and outline next steps within 24 hours
                </p>
              </div>
            </div>

            <div className="step-simple">
              <span className="step-number">3</span>
              <div>
                <h4>Solution or meeting</h4>
                <p>
                  For demos: we'll offer a time slot. For support: we'll start
                  troubleshooting.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
